import { useEffect, useRef, useState } from "react";

const DEFAULT_CENTER = {
  latitude: -23.55052,
  longitude: -46.633308,
};

const PROGRAMMATIC_MOVE_LOCK_MS = 420;

function hasValidCoordinates(latitude, longitude) {
  const parsedLatitude = Number(latitude);
  const parsedLongitude = Number(longitude);

  return (
    Number.isFinite(parsedLatitude) &&
    Number.isFinite(parsedLongitude) &&
    Math.abs(parsedLatitude) <= 90 &&
    Math.abs(parsedLongitude) <= 180
  );
}

function areCoordinatesClose(left, right, tolerance = 0.00018) {
  if (!left || !right) {
    return false;
  }
  return (
    Math.abs(Number(left.latitude ?? left.lat) - Number(right.latitude ?? right.lat)) <= tolerance &&
    Math.abs(Number(left.longitude ?? left.lng) - Number(right.longitude ?? right.lng)) <= tolerance
  );
}

function haversineDistanceKm(lat1, lon1, lat2, lon2) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const deltaLatitude = toRadians(lat2 - lat1);
  const deltaLongitude = toRadians(lon2 - lon1);
  const a =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(deltaLongitude / 2) ** 2;

  return 2 * earthRadiusKm * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function estimateViewportRadiusKm(map) {
  const bounds = map.getBounds();
  const center = map.getCenter();

  if (!bounds?.isValid?.() || !center) {
    return 3;
  }

  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  const verticalRadius = haversineDistanceKm(
    center.lat,
    center.lng,
    northEast.lat,
    center.lng
  );
  const horizontalRadius = haversineDistanceKm(
    center.lat,
    center.lng,
    center.lat,
    northEast.lng
  );
  const diagonalRadius = haversineDistanceKm(
    southWest.lat,
    southWest.lng,
    northEast.lat,
    northEast.lng
  ) / 2;

  return Math.max(0.8, Math.min(Math.max(verticalRadius, horizontalRadius, diagonalRadius), 8));
}

function buildPopupContent(market, t) {
  const pieces = [`<strong>${market.market}</strong>`];

  if (market.address) {
    pieces.push(market.address);
  }

  pieces.push(
    t?.("map.component.tap_to_use", "Toque para usar este mercado automaticamente.") ||
      "Toque para usar este mercado automaticamente."
  );
  return pieces.join("<br/>");
}

function buildClusteredMarkets(markets, zoomLevel) {
  if (markets.length <= 60 || zoomLevel >= 13) {
    return markets.map((market) => ({
      kind: "market",
      market,
      latitude: market.latitude,
      longitude: market.longitude,
    }));
  }

  const cellSize =
    zoomLevel <= 9 ? 0.06 :
    zoomLevel <= 11 ? 0.03 :
    0.016;
  const clusters = new Map();

  markets.forEach((market) => {
    const key = [
      Math.round(Number(market.latitude) / cellSize),
      Math.round(Number(market.longitude) / cellSize),
    ].join(":");
    const bucket = clusters.get(key) || [];
    bucket.push(market);
    clusters.set(key, bucket);
  });

  return [...clusters.values()].map((items) => {
    if (items.length === 1) {
      const market = items[0];
      return {
        kind: "market",
        market,
        latitude: market.latitude,
        longitude: market.longitude,
      };
    }

    const averageLatitude =
      items.reduce((sum, market) => sum + Number(market.latitude), 0) / items.length;
    const averageLongitude =
      items.reduce((sum, market) => sum + Number(market.longitude), 0) / items.length;

    return {
      kind: "cluster",
      markets: items,
      count: items.length,
      latitude: averageLatitude,
      longitude: averageLongitude,
    };
  });
}

export default function MarketMapPicker({
  markets,
  userLocation,
  selectedMarketId,
  selectedPoint,
  onMapPick,
  onMarketSelect,
  onViewportChange,
  t,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const onMapPickRef = useRef(onMapPick);
  const onMarketSelectRef = useRef(onMarketSelect);
  const onViewportChangeRef = useRef(onViewportChange);
  const userLocationRef = useRef(userLocation);
  const invalidateSizeTimeoutRef = useRef(null);
  const programmaticMoveUntilRef = useRef(0);
  const hasAutoFramedMarketsRef = useRef(false);
  const hasManualViewportRef = useRef(false);
  const lastMarketDatasetSignatureRef = useRef("");
  const layersRef = useRef({
    marketLayer: null,
    userMarker: null,
    selectedPointMarker: null,
  });
  const [isLeafletReady, setIsLeafletReady] = useState(() => Boolean(window.L));
  const [viewportRevision, setViewportRevision] = useState(0);

  useEffect(() => {
    onMapPickRef.current = onMapPick;
  }, [onMapPick]);

  useEffect(() => {
    onMarketSelectRef.current = onMarketSelect;
  }, [onMarketSelect]);

  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);

  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  useEffect(() => {
    if (window.L) {
      setIsLeafletReady(true);
      return undefined;
    }

    const interval = window.setInterval(() => {
      if (window.L) {
        window.clearInterval(interval);
        setIsLeafletReady(true);
      }
    }, 250);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isLeafletReady || mapRef.current || !mapContainerRef.current) {
      return undefined;
    }

    const L = window.L;
    const initialCenter = userLocationRef.current || DEFAULT_CENTER;
    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    }).setView([initialCenter.latitude, initialCenter.longitude], 13, {
      animate: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    layersRef.current.marketLayer = L.layerGroup().addTo(map);

    const markProgrammaticMove = (durationMs = PROGRAMMATIC_MOVE_LOCK_MS) => {
      programmaticMoveUntilRef.current = Date.now() + durationMs;
    };

    const notifyViewportChange = () => {
      const center = map.getCenter();
      setViewportRevision((current) => current + 1);
      onViewportChangeRef.current?.({
        latitude: center.lat,
        longitude: center.lng,
        radiusKm: estimateViewportRadiusKm(map),
        zoom: map.getZoom(),
      });
    };

    const handleMapClick = (event) => {
      onMapPickRef.current?.({
        latitude: event.latlng.lat,
        longitude: event.latlng.lng,
      });
    };
    const handleManualNavigation = () => {
      if (Date.now() < programmaticMoveUntilRef.current) {
        return;
      }
      hasManualViewportRef.current = true;
    };
    const handleMoveEnd = () => {
      if (Date.now() < programmaticMoveUntilRef.current) {
        return;
      }
      notifyViewportChange();
    };
    const handleResize = () => {
      if (mapRef.current !== map) {
        return;
      }
      markProgrammaticMove(260);
      map.invalidateSize({ animate: false });
    };

    map.on("click", handleMapClick);
    map.on("dragstart", handleManualNavigation);
    map.on("zoomstart", handleManualNavigation);
    map.on("moveend", handleMoveEnd);
    window.addEventListener("resize", handleResize);

    mapRef.current = map;
    invalidateSizeTimeoutRef.current = window.setTimeout(() => {
      if (mapRef.current === map) {
        markProgrammaticMove();
        map.invalidateSize({ animate: false });
        notifyViewportChange();
      }
    }, 180);

    return () => {
      if (invalidateSizeTimeoutRef.current) {
        window.clearTimeout(invalidateSizeTimeoutRef.current);
        invalidateSizeTimeoutRef.current = null;
      }
      map.off("click", handleMapClick);
      map.off("dragstart", handleManualNavigation);
      map.off("zoomstart", handleManualNavigation);
      map.off("moveend", handleMoveEnd);
      window.removeEventListener("resize", handleResize);
      map.stop();
      map.remove();
      mapRef.current = null;
    };
  }, [isLeafletReady]);

  useEffect(() => {
    const signature = markets
      .filter((market) => hasValidCoordinates(market.latitude, market.longitude))
      .slice(0, 40)
      .map((market) => market.market_id || market.market || `${market.latitude}:${market.longitude}`)
      .join("|");
    const nextSignature = `${markets.length}:${signature}`;

    if (nextSignature === lastMarketDatasetSignatureRef.current) {
      return;
    }

    lastMarketDatasetSignatureRef.current = nextSignature;
    hasAutoFramedMarketsRef.current = false;
  }, [markets]);

  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    const layerGroup = layersRef.current.marketLayer;
    if (!map || !L || !layerGroup) {
      return;
    }

    layerGroup.clearLayers();

    const validMarkets = markets.filter((market) =>
      hasValidCoordinates(market.latitude, market.longitude)
    );
    const clusterablePoints = buildClusteredMarkets(validMarkets, map.getZoom());

    clusterablePoints.forEach((point) => {
      if (point.kind === "market") {
        const market = point.market;
        const isSelected = market.market_id === selectedMarketId;
        const marker = L.circleMarker([market.latitude, market.longitude], {
          radius: isSelected ? 10 : 8,
          color: isSelected ? "#ea580c" : "#e11d48",
          fillColor: isSelected ? "#fb923c" : "#fb7185",
          fillOpacity: 0.92,
          weight: 2,
        });

        marker.bindTooltip(market.market, {
          direction: "top",
          offset: [0, -8],
        });
        marker.bindPopup(buildPopupContent(market, t), {
          autoPan: true,
        });

        marker.on("click", () => {
          marker.openPopup();
          onMarketSelectRef.current?.(market);
        });

        marker.addTo(layerGroup);
        return;
      }

      const clusterMarker = L.circleMarker([point.latitude, point.longitude], {
        radius: Math.min(18, 10 + point.count * 0.18),
        color: "#9f1239",
        fillColor: "#f97316",
        fillOpacity: 0.85,
        weight: 2,
      });
      clusterMarker.bindTooltip(
        (t?.("map.component.cluster", "{{count}} locais") || "{{count}} locais").replace(
          "{{count}}",
          String(point.count)
        ),
        {
        direction: "top",
        offset: [0, -8],
        permanent: false,
        }
      );
      clusterMarker.on("click", () => {
        const bounds = L.latLngBounds(
          point.markets.map((market) => [market.latitude, market.longitude])
        );
        programmaticMoveUntilRef.current = Date.now() + PROGRAMMATIC_MOVE_LOCK_MS;
        map.fitBounds(bounds.pad(0.35), {
          animate: false,
          maxZoom: Math.min(15, map.getZoom() + 2),
        });
      });
      clusterMarker.addTo(layerGroup);
    });
  }, [markets, selectedMarketId, t, viewportRevision]);

  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) {
      return;
    }

    if (layersRef.current.userMarker) {
      layersRef.current.userMarker.remove();
      layersRef.current.userMarker = null;
    }

    if (!userLocation) {
      return;
    }

    layersRef.current.userMarker = L.circleMarker(
      [userLocation.latitude, userLocation.longitude],
      {
        radius: 9,
        color: "#111827",
        fillColor: "#f59e0b",
        fillOpacity: 0.95,
        weight: 3,
      }
    )
      .bindTooltip(t?.("map.component.your_location", "Sua localizacao") || "Sua localizacao", {
        direction: "top",
        offset: [0, -8],
      })
      .addTo(map);
  }, [t, userLocation]);

  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) {
      return;
    }

    if (layersRef.current.selectedPointMarker) {
      layersRef.current.selectedPointMarker.remove();
      layersRef.current.selectedPointMarker = null;
    }

    if (!selectedPoint) {
      return;
    }

    layersRef.current.selectedPointMarker = L.marker([
      selectedPoint.latitude,
      selectedPoint.longitude,
    ]).addTo(map);
  }, [selectedPoint]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    if (selectedPoint) {
      const targetCoordinates = {
        latitude: selectedPoint.latitude,
        longitude: selectedPoint.longitude,
      };
      const targetZoom = Math.max(map.getZoom(), 15);
      const mapCenter = map.getCenter();
      if (
        areCoordinatesClose(mapCenter, targetCoordinates) &&
        map.getZoom() >= targetZoom
      ) {
        return;
      }
      programmaticMoveUntilRef.current = Date.now() + PROGRAMMATIC_MOVE_LOCK_MS;
      map.setView(
        [selectedPoint.latitude, selectedPoint.longitude],
        targetZoom,
        { animate: false }
      );
      return;
    }

    if (userLocation) {
      const targetCoordinates = {
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      };
      const targetZoom = Math.max(map.getZoom(), 14);
      const mapCenter = map.getCenter();
      if (
        areCoordinatesClose(mapCenter, targetCoordinates) &&
        map.getZoom() >= targetZoom
      ) {
        return;
      }
      programmaticMoveUntilRef.current = Date.now() + PROGRAMMATIC_MOVE_LOCK_MS;
      map.setView(
        [userLocation.latitude, userLocation.longitude],
        targetZoom,
        { animate: false }
      );
    }
  }, [selectedPoint, userLocation]);

  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L || userLocation || selectedPoint || hasManualViewportRef.current) {
      return;
    }

    const validMarkets = markets.filter((market) =>
      hasValidCoordinates(market.latitude, market.longitude)
    );

    if (validMarkets.length === 0) {
      return;
    }

    if (hasAutoFramedMarketsRef.current) {
      return;
    }

    const bounds = L.latLngBounds(
      validMarkets.map((market) => [market.latitude, market.longitude])
    );

    if (!bounds.isValid()) {
      return;
    }

    hasAutoFramedMarketsRef.current = true;
    programmaticMoveUntilRef.current = Date.now() + PROGRAMMATIC_MOVE_LOCK_MS;
    map.fitBounds(bounds.pad(0.2), {
      animate: false,
      maxZoom: 13,
    });
  }, [markets, selectedPoint, userLocation]);

  if (!isLeafletReady) {
    return (
      <div
        style={{
          minHeight: 380,
          borderRadius: 24,
          border: "1px solid #fecdd3",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #fff1f2 0%, #ffe4e6 100%)",
          color: "#881337",
        }}
      >
        {t?.("map.component.loading", "Carregando mapa...") || "Carregando mapa..."}
      </div>
    );
  }

  return (
    <div
      ref={mapContainerRef}
      style={{
        minHeight: 380,
        width: "100%",
        borderRadius: 24,
        overflow: "hidden",
        border: "1px solid #fecdd3",
      }}
    />
  );
}
